import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { types } from "mobx-state-tree";

/**
 * Upload status enumeration for tracking file upload states.
 */
export const UploadStatus = types.enumeration("UploadStatus", [
  "pending",
  "uploading",
  "verifying",
  "complete",
  "failed",
  "retrying",
]);

/**
 * Model for a file queued for upload.
 * Tracks metadata and upload state without storing file content.
 * Actual File handles are stored in volatile state to avoid serialization issues.
 */
export const QueuedFile = types
  .model("QueuedFile", {
    /** Unique identifier for this queued file */
    id: types.identifier,
    batchId: types.optional(types.string, ""),
    /** Original filename */
    fileName: types.string,
    /** Original file size in bytes (used for verification) */
    fileSize: types.number,
    /** Target storage path in Supabase */
    filePath: types.string,
    /** Quota admission held for this exact storage path until document creation. */
    reservationId: types.optional(types.maybeNull(types.string), null),
    /** Server expiry for the held quota admission. */
    reservationExpiresAt: types.optional(types.maybeNull(types.string), null),
    /** Relative path from drag-drop (preserves folder structure) */
    relativePath: types.maybeNull(types.string),
    /** Current upload status */
    status: UploadStatus,
    /** Number of retry attempts */
    retryCount: types.optional(types.number, 0),
    /** Error message if upload failed */
    error: types.maybeNull(types.string),
    /** Upload progress percentage (0-100) */
    progress: types.optional(types.number, 0),
    /** File MIME type */
    contentType: types.string,
    /** Timestamp when file was added to queue */
    addedAt: types.string,
    /** Timestamp when upload started */
    startedAt: types.maybeNull(types.string),
    /** Timestamp when upload completed (or failed permanently) */
    completedAt: types.maybeNull(types.string),
    /**
     * Per-file configuration to prevent config race condition.
     * Each file stores its own buildingId, orgId, projectId so that
     * files from different projects don't get mixed up.
     */
    buildingId: types.string,
    orgId: types.string,
    projectId: types.maybeNull(types.string),
    /** Parent folder ID for uploads into a selected folder */
    parentFolderId: types.maybeNull(types.string),
    /** SHA256 hash of file content for versioning duplicate detection */
    contentHash: types.maybeNull(types.string),
    /** Embedded creation date from file metadata (EXIF, PDF, Office XML) as ISO string */
    embeddedCreatedAt: types.maybeNull(types.string),
    /** Embedded modification date from file metadata as ISO string */
    embeddedModifiedAt: types.maybeNull(types.string),
    /**
     * Whether a document record has been created for this file.
     * Persists in the MST store across React remounts, preventing
     * duplicate document creation when navigating away and back.
     */
    documentCreated: types.optional(types.boolean, false),
    /** Registration is in flight or unconfirmed; retain the uploaded object and admission. */
    registrationUncertain: types.optional(types.boolean, false),
    /**
     * Whether this failed file can be retried.
     * Non-retryable failures (e.g. unsupported file type) should not show retry options.
     * Defaults to true so that network/server errors remain retryable.
     */
    retryable: types.optional(types.boolean, true),
  })
  .actions((self) => ({
    markRegistrationInFlight() {
      self.registrationUncertain = true;
    },
    setDocumentCreated() {
      self.documentCreated = true;
      self.registrationUncertain = false;
      self.reservationId = null;
      self.reservationExpiresAt = null;
    },
    clearReservation() {
      self.reservationId = null;
      self.reservationExpiresAt = null;
    },
    setDocumentCreationFailed(message: string) {
      self.status = "failed";
      self.registrationUncertain = false;
      self.error = message;
      self.completedAt = new Date().toISOString();
      self.reservationId = null;
      self.reservationExpiresAt = null;
    },
    setDocumentCreationUncertain(message: string) {
      self.status = "failed";
      self.error = message;
      self.completedAt = new Date().toISOString();
      self.retryable = false;
      self.registrationUncertain = true;
    },
  }));

export type QueuedFileInstance = Instance<typeof QueuedFile>;
export type QueuedFileSnapshotIn = SnapshotIn<typeof QueuedFile>;
export type QueuedFileSnapshotOut = SnapshotOut<typeof QueuedFile>;

export type UploadStatusType = Instance<typeof UploadStatus>;

/**
 * Reason why a file was skipped during pre-upload validation.
 */
export type SkipReason = "empty" | "unreadable" | "unsupported_type" | "too-large";

/**
 * Represents a file that was skipped during pre-upload validation.
 * Stored in volatile state (not an MST model) since we don't need persistence.
 */
export interface SkippedFile {
  /** Original filename */
  fileName: string;
  /** Original file size in bytes */
  fileSize: number;
  /** Reason the file was skipped */
  reason: SkipReason;
  /** Human-readable description of why the file was skipped */
  reasonDescription: string;
}

/**
 * Summary returned from addFiles action.
 */
export interface AddFilesSummary {
  /** Number of files successfully added to the queue */
  added: number;
  /** Number of files skipped due to validation failures */
  skipped: number;
  /** Breakdown of skipped files by reason */
  skippedByReason: {
    empty: number;
    unreadable: number;
    unsupported_type: number;
    too_large: number;
  };
}
